import { useMemo } from 'react';

import { useStatisticsBatch } from '@/hooks/useStatisticsBatch';
import { parseTechnologyProductName } from '@/sections/technology/utils';
import type { DropdownOption } from '@/types/dropdown.types';
import { formatPrettyName } from '@/utils/mappings.util';
import { buildMergedFilterGroups } from '@/utils/mergeFilterOptions.util';

/**
 * The hook's return type is explicitly defined to ensure type safety
 * for consuming components like the QueryBuilder.
 *
 * Vendor/product/version options are MERGED by display label
 * (mergeFilterOptions.util.ts): option `value`/`id` is the canonical label
 * and `rawValues` carries every raw backend variant. Consumers must expand
 * selections back to raws at apply time (expandMergedFilterValues) and
 * collapse stored raws for selection display (collapseMergedFilterValues) —
 * the backend matches exactly and case-sensitively. Tags keep the raw-value
 * contract (no rawValues).
 */
export interface TechnologyFilterOptions {
  vendor: (DropdownOption & { rawValues: string[] })[];
  product: (DropdownOption & { rawValues: string[] })[];
  version: (DropdownOption & { rawValues: string[] })[];
  tags: DropdownOption[];
  [key: string]: DropdownOption[];
}

/**
 * Formats version strings for display by removing backslashes
 * This ensures consistency with how versions are displayed elsewhere in the app
 */
const formatVersionForDisplay = (version: string): string => {
  // Remove backslashes and apply pretty formatting
  const cleanedVersion = version.split('\\').join('');
  return formatPrettyName(cleanedVersion);
};

export const useGetTechnologyFilters = (): {
  data: TechnologyFilterOptions;
  status: 'pending' | 'error' | 'success';
} => {
  /**
   * PERFORMANCE OPTIMIZATION: Batch statistics query
   *
   * Replaces 4 separate useMy() calls with a single batch request for massive performance improvement.
   *
   * Before: 4 separate API requests × 500-1500ms each = 2-6 seconds total
   * After: 1 batch API request = 50-200ms
   * Improvement: 90-97% faster
   *
   * Batched queries:
   * - technology#vendor#now
   * - technology#product#now
   * - technology#version#now
   * - technology#tags#now
   */
  const { data: batchStatistics, status: batchStatisticsStatus } =
    useStatisticsBatch({
      statisticKeys: [
        'technology#vendor#now',
        'technology#product#now',
        'technology#version#now',
        'technology#tags#now',
      ],
      enabled: true,
      staleTime: 5 * 60 * 1000, // 5 minutes (matches original queries)
    });

  const options = useMemo((): TechnologyFilterOptions => {
    // Extract individual statistics from batch response with fallback to empty arrays
    // Extraction happens inside useMemo to satisfy exhaustive-deps
    const vendorStatistics = batchStatistics?.['technology#vendor#now'] || [];
    const productStatistics = batchStatistics?.['technology#product#now'] || [];
    const versionStatistics = batchStatistics?.['technology#version#now'] || [];
    const tagStatistics = batchStatistics?.['technology#tags#now'] || [];

    /**
     * Builds merged options: raws whose label fn output is byte-identical
     * collapse into ONE option (value/id/label = canonical label, all raw
     * variants in `rawValues`). Statistics order is preserved (first-seen
     * group order, no new sort); raws whose label formats to empty are
     * dropped.
     */
    const toMergedOptions = (
      statistics: { value: string; name: string }[],
      formatLabel: (raw: string) => string | null
    ) =>
      buildMergedFilterGroups(
        statistics.map(statistic => String(statistic.value)),
        formatLabel
      ).map(group => ({
        id: group.value,
        value: group.value,
        label: group.value,
        rawValues: group.rawValues,
      }));

    return {
      /**
       * Vendor labels remove backslash escape sequences (same unescaping as
       * parseTechnologyProductName) to handle CPE format
       * (e.g., "react\ project" -> "React Project").
       */
      vendor: toMergedOptions(
        vendorStatistics || [],
        raw => formatPrettyName(raw.replace(/\\(.)/g, '$1')) || null
      ),
      /**
       * Product labels normalize CPE names via parseTechnologyProductName,
       * then formatPrettyName for consistent display with table cells and
       * drawer titles. The raw values are preserved in `rawValues` for API
       * filtering (backend expects exact match).
       */
      product: toMergedOptions(
        productStatistics || [],
        raw => formatPrettyName(parseTechnologyProductName(raw)) || null
      ),
      // Version labels strip backslashes (formatVersionForDisplay)
      version: toMergedOptions(
        versionStatistics || [],
        raw => formatVersionForDisplay(raw) || null
      ),
      tags: (tagStatistics || [])
        .filter(
          stat => stat.value && stat.value !== '' && stat.value !== 'null'
        )
        .sort((a, b) => a.value.localeCompare(b.value))
        .map(stat => ({
          id: `tag-${stat.value}`,
          label: stat.value,
          value: stat.value,
        })),
    };
  }, [batchStatistics]);

  return { data: options, status: batchStatisticsStatus };
};
